import { useForm } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { miloAuth } from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { routes } from "../routes";
import { TextField } from "../components/form";

type FormValues = { login: string; password: string };

export default function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { t } = useTheme();

  const { control, handleSubmit } = useForm<FormValues>({
    defaultValues: { login: "", password: "" },
  });

  // La connexion est un appel qui modifie l'état (dépôt du token) : c'est une
  // mutation. useMutation gère pour nous le chargement (isPending) et l'erreur.
  const loginMut = useMutation({
    mutationFn: (values: FormValues) => miloAuth.login(values.login, values.password),
    onSuccess: (token) => {
      auth.setToken(token);
      navigate(routes.home(), { replace: true });
    },
  });

  const error = loginMut.error
    ? loginMut.error instanceof Error
      ? loginMut.error.message
      : "Erreur inconnue"
    : null;

  const onSubmit = handleSubmit((values) => loginMut.mutate(values));

  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      background: t.mainBg,
    }}>
      <div style={{
        width: 380, padding: 32, background: t.cardBg, borderRadius: 14,
        boxShadow: "0 20px 60px rgba(0,0,0,.25)", border: `1px solid ${t.rowBorder}`,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 22 }}>
          <div style={{
            width: 42, height: 42, borderRadius: 11,
            background: "linear-gradient(140deg,#330072,#0072CE)",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "#fff", fontWeight: 700, fontSize: 16,
            boxShadow: "0 4px 16px rgba(51,0,114,.35)",
          }}>W</div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 700, color: t.text1 }}>WSD Admin</div>
            <div style={{ fontSize: 12, color: t.text2 }}>Connexion via Milo</div>
          </div>
        </div>

        <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <TextField
            control={control}
            name="login"
            label="Identifiant"
            autoComplete="username"
            autoFocus
            rules={{ required: "Identifiant obligatoire" }}
          />
          <TextField
            control={control}
            name="password"
            type="password"
            label="Mot de passe"
            autoComplete="current-password"
            rules={{ required: "Mot de passe obligatoire" }}
          />
          {error && (
            <div style={{
              padding: "10px 12px", background: t.lateBg, color: t.lateColor,
              borderRadius: 8, fontSize: 13,
            }}>{error}</div>
          )}
          <button
            type="submit"
            disabled={loginMut.isPending}
            style={{
              padding: "11px 16px", borderRadius: 8, border: "none",
              background: t.btnBg, color: "#fff", fontSize: 14, fontWeight: 600,
              cursor: loginMut.isPending ? "not-allowed" : "pointer", fontFamily: "inherit",
              marginTop: 6, opacity: loginMut.isPending ? 0.7 : 1,
            }}
          >
            {loginMut.isPending ? "Connexion..." : "Se connecter"}
          </button>
        </form>
      </div>
    </div>
  );
}
